import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { parseLocalDate } from "@/lib/utils";
import { format, differenceInDays, isAfter, isSameDay, startOfDay } from "date-fns";

export interface RegistrationDeadlineData {
  deadline: Date;
  isExpired: boolean;
  isLastDay: boolean;
  daysRemaining: number;
  formattedDeadline: string;
  urgencyMessage: string;
}

export function useRegistrationDeadline() {
  return useQuery({
    queryKey: ["registration-deadline"],
    queryFn: async (): Promise<RegistrationDeadlineData | null> => {
      const { data, error } = await supabase
        .from("settings")
        .select("value")
        .eq("key", "REGISTRATION_DEADLINE")
        .maybeSingle();

      if (error || !data?.value) {
        return null;
      }

      const deadline = parseLocalDate(data.value);
      const today = startOfDay(new Date());

      const isExpired = isAfter(today, deadline);
      const isLastDay = isSameDay(today, deadline);
      const daysRemaining = differenceInDays(deadline, today);

      let urgencyMessage = "";
      if (isLastDay) {
        urgencyMessage = "Last day to register!";
      } else if (daysRemaining > 0) {
        urgencyMessage = `Register by ${format(deadline, "MMM d")}`;
      }

      return {
        deadline,
        isExpired,
        isLastDay,
        daysRemaining,
        formattedDeadline: format(deadline, "MMM d"),
        urgencyMessage,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
